/**
 * GoalAlertsPanel.js
 * ==================
 *
 * Goal > Alerts. User-defined alert rules evaluated by the backend
 * every time the goal's champion produces a forecast.
 *
 * Surface:
 *   * Rule list — one card per rule with its condition sentence, on/off
 *     switch, severity, delivery, last-evaluation status and trigger
 *     stats. Live: refreshes on the goal's `alerts.evaluated` realtime
 *     event.
 *   * Builder dialog — create/edit with a LIVE preview: while the user
 *     tunes the condition, the panel dry-runs it against the champion's
 *     latest forecast (`POST .../alerts/preview`) and draws the
 *     forecast path with the threshold line, saying plainly whether the
 *     rule would fire right now.
 *   * Recipients — on organization workspaces, other org members can be
 *     added as recipients; deliveries then honor each person's own
 *     notification preferences (or the rule's pinned channels).
 *
 * REST (via redux-saga slices):
 *   GET    /goals/:goalId/alerts                        -> discovery_alerts
 *   POST   /goals/:goalId/alerts                        -> discovery_save_alert
 *   PUT    /goals/:goalId/alerts/:alertId               -> discovery_save_alert / discovery_toggle_alert
 *   DELETE /goals/:goalId/alerts/:alertId               -> discovery_delete_alert
 *   POST   /goals/:goalId/alerts/preview                -> discovery_preview_alert
 *   GET    /organization/mine + /organization/:id/members -> discovery_alert_org_members
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, IconButton,
  InputLabel, MenuItem, Paper, Select, Stack, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';

import ChannelIcon, { CHANNEL_META } from '../settings/ChannelIcons';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';
import {
  fetchRequest as alertsRequest,
  clearState as alertsClear,
} from '../../redux/actions/discovery/alerts';
import {
  fetchRequest as saveAlertRequest,
  clearState as saveAlertClear,
} from '../../redux/actions/discovery/saveAlert';
import { fetchRequest as toggleAlertRequest } from '../../redux/actions/discovery/toggleAlert';
import { fetchRequest as deleteAlertRequest } from '../../redux/actions/discovery/deleteAlert';
import {
  fetchRequest as previewAlertRequest,
  clearState as previewAlertClear,
} from '../../redux/actions/discovery/previewAlert';
import { fetchRequest as alertOrgMembersRequest } from '../../redux/actions/discovery/alertOrgMembers';

// The metric set depends on the goal type: value/distribution goals serve
// the target's own level, while trend/anomaly goals serve a probability in
// [0, 1] — level metrics (change vs an anchor, crossings, quantile width)
// simply don't exist on a likelihood path. Mirrors the BE's
// metrics_for_goal_type; the BE also rejects mismatches on save.
const LEVEL_METRICS = [
  {
    key: 'forecast_value',
    label: 'Forecast level',
    hint: 'The forecast value itself — "warn me when the forecast tops 4,000".',
  },
  {
    key: 'forecast_change_pct',
    label: 'Change vs now (%)',
    hint: 'Percent move of the forecast against the latest actual value.',
  },
  {
    key: 'crossing',
    label: 'Level crossing',
    hint: 'Fires only when the forecast crosses a level it is currently on the other side of.',
  },
  {
    key: 'uncertainty_pct',
    label: 'Uncertainty width (%)',
    hint: 'How wide the p10–p90 band is relative to the median — a "model is unsure" alarm.',
  },
];

const PROBABILITY_METRIC = {
  trend: {
    key: 'probability',
    label: 'Upward-trend likelihood (%)',
    hint: 'The model\u2019s probability that the target trends upward — "alert me when upward likelihood tops 80%".',
  },
  anomaly: {
    key: 'probability',
    label: 'Anomaly likelihood (%)',
    hint: 'The model\u2019s probability that the target behaves anomalously — "page me when anomaly likelihood tops 80%".',
  },
};

// Fleet goals serve a ranked leaderboard, not a forecast path — their
// alert vocabulary is board-shaped. Mirrors the BE's _FLEET_METRICS.
const FLEET_METRICS = [
  {
    key: 'member_score',
    label: 'Any member\u2019s predicted growth / risk (%)',
    hint: 'Fires when any member crosses the threshold — "page me when anything is expected to grow more than 10%" or "when failure risk tops 90%".',
    pct: true,
  },
  {
    key: 'top_decile_entries',
    label: 'New entries in the top 10%',
    hint: 'How many members ENTERED the top decile this run — a churn alarm for the top of the board.',
  },
  {
    key: 'rank_jump',
    label: 'Rank jump (positions)',
    hint: 'Fires when a member climbs more than this many places in one run — the "sudden mover" alarm.',
  },
  {
    key: 'fleet_median_score',
    label: 'Fleet median growth / risk (%)',
    hint: 'The typical member is drifting — the median score crosses the threshold.',
    pct: true,
  },
];

// Event-native rules — evaluated on event arrival / shock entry / the live
// quiet sweep, NOT on the forecast path. Available on every goal type
// (mirrors the BE's EVENT_METRICS). Operators/scopes are fixed server-side:
// "at or above" is the only reading that makes sense for all three.
const EVENT_METRICS = [
  {
    key: 'event_fired',
    label: 'Important event on a stream',
    hint: 'Fires the moment an event on the chosen stream scores at or above this importance — "tell me when something big happens on news".',
    event: true,
  },
  {
    key: 'shock_entered',
    label: 'Goal enters shock mode',
    hint: 'Fires when an important event flips this goal into shock-mode serving (blended forecast, widened intervals). Optionally restrict to one stream.',
    event: true,
  },
  {
    key: 'event_quiet',
    label: 'Stream goes quiet',
    hint: 'Fires when the chosen stream has recorded no event for this many hours — a dead-feed / silence alarm.',
    event: true,
  },
];
const isEventMetric = (key) => EVENT_METRICS.some((m) => m.key === key);

const CLASSIFICATION_GOAL_TYPES = ['trend', 'anomaly'];
const isClassificationGoal = (goalType) => CLASSIFICATION_GOAL_TYPES.includes(goalType);
const FLEET_GOAL_TYPES = ['rank_by_risk', 'rank_by_growth'];
const isFleetGoal = (goalType) => FLEET_GOAL_TYPES.includes(goalType);

const metricsForGoalType = (goalType) => {
  if (isFleetGoal(goalType)) return [...FLEET_METRICS, ...EVENT_METRICS];
  return isClassificationGoal(goalType)
    ? [PROBABILITY_METRIC[goalType] || PROBABILITY_METRIC.trend, ...EVENT_METRICS]
    : [...LEVEL_METRICS, ...EVENT_METRICS];
};

const OPERATORS = [
  { key: 'gt', label: 'above' },
  { key: 'gte', label: 'at or above' },
  { key: 'lt', label: 'below' },
  { key: 'lte', label: 'at or below' },
];

const SCOPES = [
  { key: 'any', label: 'at any point' },
  { key: 'last', label: 'at horizon end' },
  { key: 'min', label: 'at its lowest' },
  { key: 'max', label: 'at its highest' },
  { key: 'mean', label: 'on average' },
];

const COOLDOWNS = [
  { minutes: 0, label: 'Every forecast' },
  { minutes: 60, label: '1 hour' },
  { minutes: 240, label: '4 hours' },
  { minutes: 1440, label: '24 hours' },
  { minutes: 10080, label: '7 days' },
];
const FLEET_COOLDOWNS = [
  { minutes: 0, label: 'Every scoring run' },
  { minutes: 60, label: '1 hour' },
  { minutes: 240, label: '4 hours' },
  { minutes: 1440, label: '24 hours' },
  { minutes: 10080, label: '7 days' },
];

const PINNABLE_CHANNELS = ['in_app', 'email', 'slack', 'teams', 'pagerduty', 'webhook', 'websocket'];

const metricOf = (key, goalType) => {
  const list = metricsForGoalType(goalType);
  return list.find((m) => m.key === key) || list[0];
};

// Client-side condition sentence — mirrors the BE's condition_label so
// the list renders without a round-trip.
const conditionSentence = (condition, unit, goalType) => {
  if (!condition) return '';
  const op = OPERATORS.find((o) => o.key === condition.operator)?.label || condition.operator;
  const scope = SCOPES.find((s) => s.key === condition.scope)?.label || '';
  const thr = Number(condition.threshold);
  const thrTxt = Number.isFinite(thr) ? thr.toLocaleString() : condition.threshold;
  const u = unit ? ` ${unit}` : '';
  switch (condition.metric) {
    case 'forecast_value':
      return `Forecast ${op} ${thrTxt}${u} ${scope}`;
    case 'forecast_change_pct':
      return `Forecast change ${op} ${thrTxt}% ${scope}`;
    case 'uncertainty_pct':
      return `Uncertainty band ${op} ${thrTxt}% ${scope}`;
    case 'crossing':
      return `Forecast crosses ${['gt', 'gte'].includes(condition.operator) ? 'above' : 'below'} ${thrTxt}${u}`;
    case 'probability':
      return `${goalType === 'anomaly' ? 'Anomaly' : 'Upward-trend'} likelihood ${op} ${thrTxt}% ${scope}`;
    case 'event_fired':
      return `An event on '${condition.stream}' scores importance ${op} ${thrTxt}%`;
    case 'shock_entered':
      return condition.stream
        ? `Goal enters shock mode on a '${condition.stream}' event`
        : 'Goal enters shock mode';
    case 'event_quiet':
      return `'${condition.stream}' goes quiet for ${op} ${thrTxt} hours`;
    case 'member_score':
      return `Any member's ${goalType === 'rank_by_risk' ? 'failure risk' : 'predicted growth'} ${op} ${thrTxt}%`;
    case 'top_decile_entries':
      return `Members entering the top decile ${op} ${thrTxt}`;
    case 'rank_jump':
      return `A member climbs ${op} ${thrTxt} positions`;
    case 'fleet_median_score':
      return `Fleet median ${goalType === 'rank_by_risk' ? 'failure risk' : 'predicted growth'} ${op} ${thrTxt}%`;
    default:
      return '';
  }
};

const fmtWhen = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return null;
  }
};


// ---------------------------------------------------------------------------
// Live preview mini-chart: forecast path + threshold line + anchor dot.
// Plain SVG on purpose — tiny, no chart-library overhead in a dialog.
// ---------------------------------------------------------------------------

const PreviewChart = ({ preview, condition }) => {
  const theme = useTheme();
  const W = 460;
  const H = 120;
  const PAD = 8;

  const body = useMemo(() => {
    const f = preview?.forecast;
    if (!f?.path?.length) return null;
    const isPct = ['forecast_change_pct', 'uncertainty_pct'].includes(condition?.metric);
    const path = f.path;
    const anchor = f.anchor;
    // Probability paths are plotted in [0,1] but thresholds are entered as
    // percent — rescale so the line lands on the same axis as the path.
    const rawThr = Number(condition?.threshold);
    const thr = condition?.metric === 'probability' ? rawThr / 100 : rawThr;
    // The threshold only lives on the value axis for value/crossing/
    // probability metrics (percent-of-anchor metrics have no level).
    const thrOnAxis = !isPct && Number.isFinite(thr);

    const values = [...path];
    if (anchor != null) values.push(anchor);
    if (thrOnAxis) values.push(thr);
    (f.p10 || []).forEach((v) => values.push(v));
    (f.p90 || []).forEach((v) => values.push(v));
    let lo = Math.min(...values);
    let hi = Math.max(...values);
    if (lo === hi) { lo -= 1; hi += 1; }
    const range = hi - lo;
    lo -= range * 0.08; hi += range * 0.08;

    const n = path.length + (anchor != null ? 1 : 0);
    const x = (i) => PAD + (i / Math.max(1, n - 1)) * (W - PAD * 2);
    const y = (v) => H - PAD - ((v - lo) / (hi - lo)) * (H - PAD * 2);

    const pts = [];
    let idx = 0;
    if (anchor != null) { pts.push([x(0), y(anchor)]); idx = 1; }
    path.forEach((v, i) => pts.push([x(i + idx), y(v)]));
    const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

    // Uncertainty band polygon when quantiles exist.
    let band = null;
    if (f.p10?.length && f.p90?.length) {
      const m = Math.min(f.p10.length, f.p90.length, path.length);
      const up = [];
      const dn = [];
      for (let i = 0; i < m; i += 1) {
        up.push(`${x(i + idx).toFixed(1)},${y(f.p90[i]).toFixed(1)}`);
        dn.unshift(`${x(i + idx).toFixed(1)},${y(f.p10[i]).toFixed(1)}`);
      }
      band = `M${up.join(' L')} L${dn.join(' L')} Z`;
    }

    return {
      line,
      band,
      anchorPt: anchor != null ? [x(0), y(anchor)] : null,
      thrY: thrOnAxis ? y(thr) : null,
    };
  }, [preview, condition]);

  if (!body) return null;

  const triggered = !!preview?.triggered;
  const accent = triggered ? theme.palette.warning.main : theme.palette.primary.main;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      {body.band && (
        <path d={body.band} fill={alpha(accent, 0.10)} stroke="none" />
      )}
      {body.thrY != null && (
        <line
          x1={PAD} x2={W - PAD} y1={body.thrY} y2={body.thrY}
          stroke={theme.palette.error.main} strokeWidth="1.25"
          strokeDasharray="5 4"
        />
      )}
      <path d={body.line} fill="none" stroke={accent} strokeWidth="1.75" />
      {body.anchorPt && (
        <circle
          cx={body.anchorPt[0]} cy={body.anchorPt[1]} r="3.25"
          fill={theme.palette.text.primary}
        />
      )}
    </svg>
  );
};


// ---------------------------------------------------------------------------
// Builder dialog
// ---------------------------------------------------------------------------

const emptyDraft = (goalType) => ({
  name: '',
  severity: 'warning',
  condition: {
    metric: metricsForGoalType(goalType)[0].key,
    operator: 'gt',
    threshold: '',
    scope: 'any',
    stream: '',
  },
  cooldown_minutes: 240,
  channels: null,          // null -> recipient notification preferences
  recipients: [],
});

const AlertBuilderDialog = ({
  open, goalId, goalType, alert: editing, orgMembers, currentUserId,
  eventStreams, onClose, onSaved,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [draft, setDraft] = useState(emptyDraft());
  const previewTimer = useRef(null);

  const saveState = useSelector((s) => s.discovery_save_alert);
  const previewState = useSelector((s) => s.discovery_preview_alert);
  const saving = Boolean(saveState?.isLoading);
  const saveError = saveState?.error || null;
  const previewLoading = Boolean(previewState?.isLoading);
  // A failed preview hides the chart (matches the old setPreview(null)
  // in the catch); REQUEST clears the slice error, so recovery is automatic.
  const preview = previewState?.error ? null : (previewState?.response || null);

  useEffect(() => {
    if (!open) return;
    dispatch(saveAlertClear());
    dispatch(previewAlertClear());
    if (editing) {
      // A stale metric (rule created before the goal type changed) is
      // coerced to the goal type's set — the BE refuses it anyway.
      const allowed = metricsForGoalType(goalType).map((m) => m.key);
      const savedMetric = editing.condition?.metric;
      setDraft({
        name: editing.name || '',
        severity: editing.severity || 'warning',
        condition: {
          metric: allowed.includes(savedMetric) ? savedMetric : allowed[0],
          operator: editing.condition?.operator || 'gt',
          threshold: editing.condition?.threshold ?? '',
          scope: editing.condition?.scope || 'any',
          stream: editing.condition?.stream || '',
        },
        cooldown_minutes: editing.cooldown_minutes ?? 240,
        channels: editing.channels || null,
        recipients: editing.recipients || [],
      });
    } else {
      setDraft(emptyDraft(goalType));
    }
  }, [open, editing, goalType, dispatch]);

  const condition = draft.condition;
  const isProbability = condition.metric === 'probability';
  const isFleet = isFleetGoal(goalType);
  const isEventRule = isEventMetric(condition.metric);
  const isShockRule = condition.metric === 'shock_entered';
  const thresholdNum = Number(condition.threshold);
  // Event rules have their own shape: a stream instead of operator/scope,
  // and thresholds in the metric's own unit (importance % / quiet hours);
  // shock_entered needs neither (the BE forces threshold to 0).
  const conditionComplete = isEventRule
    ? (isShockRule
      || (Boolean(condition.stream)
        && condition.threshold !== '' && Number.isFinite(thresholdNum)
        && (condition.metric !== 'event_fired' || (thresholdNum >= 0 && thresholdNum <= 100))
        && (condition.metric !== 'event_quiet' || thresholdNum >= 1)))
    : Boolean(condition.metric && condition.operator
      && condition.threshold !== '' && Number.isFinite(thresholdNum)
      // Probability thresholds are percent likelihoods.
      && (!isProbability || (thresholdNum >= 0 && thresholdNum <= 100)));

  // Debounced live preview whenever the condition is complete. The saga
  // (takeLatest) dry-runs the condition against the champion's latest
  // forecast; failures stay silent and simply hide the chart. Fleet
  // conditions evaluate against a leaderboard, not a forecast path — no
  // path preview exists for them. Event rules DO preview (against the
  // stream's / goal's current event state, no forecast involved).
  useEffect(() => {
    if (!open || !conditionComplete || (isFleet && !isEventRule)) { dispatch(previewAlertClear()); return undefined; }
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      dispatch(previewAlertRequest({
        goalId,
        condition: {
          ...condition,
          threshold: isShockRule ? 0 : Number(condition.threshold),
        },
      }));
    }, 450);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, goalId, condition.metric, condition.operator, condition.threshold, condition.scope, condition.stream]);

  const setCondition = (patch) =>
    setDraft((d) => ({ ...d, condition: { ...d.condition, ...patch } }));

  const toggleChannel = (key) => {
    setDraft((d) => {
      const current = d.channels || [];
      const next = current.includes(key)
        ? current.filter((c) => c !== key)
        : [...current, key];
      return { ...d, channels: next.length ? next : null };
    });
  };

  const toggleRecipient = (uid) => {
    setDraft((d) => {
      const current = d.recipients || [];
      const next = current.includes(uid)
        ? current.filter((u) => u !== uid)
        : [...current, uid];
      return { ...d, recipients: next };
    });
  };

  const canSave = draft.name.trim() && conditionComplete && !saving;

  const handleSave = () => {
    const cleanedCondition = isEventRule
      ? {
        metric: condition.metric,
        operator: 'gte',
        scope: 'any',
        threshold: isShockRule ? 0 : Number(condition.threshold),
        ...(condition.stream ? { stream: condition.stream } : {}),
      }
      : { ...condition, threshold: Number(condition.threshold) };
    if (!isEventRule) delete cleanedCondition.stream;
    const payload = {
      name: draft.name.trim(),
      severity: draft.severity,
      condition: cleanedCondition,
      cooldown_minutes: draft.cooldown_minutes,
      channels: draft.channels,
      recipients: draft.recipients,
    };
    dispatch(saveAlertRequest({
      goalId,
      alertId: editing ? editing.alert_id : null,
      data: payload,
    }));
  };

  // Save landed: refresh the list and close (errors stay in the dialog
  // via `saveError` above).
  useEffect(() => {
    if (!saveState?.response) return;
    dispatch(saveAlertClear());
    onSaved();
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveState?.response]);

  // Don't leak dialog-scoped request state into the next mount.
  useEffect(() => () => {
    dispatch(saveAlertClear());
    dispatch(previewAlertClear());
  }, [dispatch]);

  const goalMetrics = metricsForGoalType(goalType);
  const metric = metricOf(condition.metric, goalType);
  const isCrossing = condition.metric === 'crossing';
  const isPct = ['forecast_change_pct', 'uncertainty_pct', 'member_score', 'fleet_median_score'].includes(condition.metric)
    || isProbability;
  const unit = preview?.unit || '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <NotificationsActiveIcon sx={{ fontSize: 22 }} />
        {editing ? 'Edit alert' : 'New alert'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            size="small"
            label="Alert name"
            placeholder={isClassificationGoal(goalType)
              ? (goalType === 'anomaly'
                ? 'e.g. Anomaly likelihood above 80%'
                : 'e.g. Upward trend above 80%')
              : 'e.g. Price above 4,000'}
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            fullWidth
          />

          {/* ---- condition ---- */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              CONDITION
            </Typography>
            <Stack spacing={1.25} sx={{ mt: 0.75 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>What to watch</InputLabel>
                <Select
                  label="What to watch"
                  value={condition.metric}
                  onChange={(e) => setCondition({ metric: e.target.value })}
                >
                  {goalMetrics.map((m) => (
                    <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
                {metric.hint}
              </Typography>

              {isEventRule ? (
                // Event rules: a stream picker instead of direction/scope
                // (the BE fixes operator to "at or above"), thresholds in
                // the metric's own unit.
                <Stack direction="row" spacing={1}>
                  <FormControl
                    size="small" sx={{ flex: 1 }}
                    required={!isShockRule}
                  >
                    <InputLabel>Event stream</InputLabel>
                    <Select
                      label="Event stream"
                      value={condition.stream || ''}
                      onChange={(e) => setCondition({ stream: e.target.value })}
                    >
                      {isShockRule && (
                        <MenuItem value="">
                          <em>Any stream</em>
                        </MenuItem>
                      )}
                      {(eventStreams || []).map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!isShockRule && (
                    <TextField
                      size="small"
                      label={condition.metric === 'event_fired'
                        ? 'Min importance (%)'
                        : 'Quiet for (hours)'}
                      type="number"
                      value={condition.threshold}
                      onChange={(e) => setCondition({ threshold: e.target.value })}
                      inputProps={condition.metric === 'event_fired'
                        ? { min: 0, max: 100, step: 1 }
                        : { min: 1, step: 1 }}
                      sx={{ width: 170 }}
                    />
                  )}
                </Stack>
              ) : (
                <Stack direction="row" spacing={1}>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Direction</InputLabel>
                    <Select
                      label="Direction"
                      value={condition.operator}
                      onChange={(e) => setCondition({ operator: e.target.value })}
                    >
                      {(isCrossing
                        ? [{ key: 'gt', label: 'above' }, { key: 'lt', label: 'below' }]
                        : OPERATORS
                      ).map((o) => (
                        <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label={`Threshold${isPct ? ' (%)' : unit ? ` (${unit})` : ''}`}
                    type="number"
                    value={condition.threshold}
                    onChange={(e) => setCondition({ threshold: e.target.value })}
                    inputProps={isProbability ? { min: 0, max: 100, step: 1 } : undefined}
                    sx={{ flex: 1 }}
                  />
                  {/* Fleet conditions read one number per run — no path scope. */}
                  {!isCrossing && !isFleet && (
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Where</InputLabel>
                      <Select
                        label="Where"
                        value={condition.scope}
                        onChange={(e) => setCondition({ scope: e.target.value })}
                      >
                        {SCOPES.map((s) => (
                          <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Stack>
              )}
              {isEventRule && (eventStreams || []).length === 0 && !isShockRule && (
                <Typography variant="caption" color="text.secondary">
                  No event streams in this workspace yet — record an event first
                  (Signals › Record an event).
                </Typography>
              )}
            </Stack>
          </Box>

          {/* ---- live preview ---- */}
          {conditionComplete && isFleet && !isEventRule && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
              <Typography variant="caption" color="text.secondary">
                Checked every time the fleet is scored — same as a forecast
                rule on a single goal. There is no single path to preview;
                the condition reads the latest leaderboard.
              </Typography>
            </Paper>
          )}
          {conditionComplete && !(isFleet && !isEventRule) && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderColor: preview?.triggered
                  ? alpha(theme.palette.warning.main, 0.6)
                  : undefined,
                bgcolor: preview?.triggered
                  ? alpha(theme.palette.warning.main, 0.04)
                  : alpha(theme.palette.text.primary, 0.02),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                {previewLoading && <CircularProgress size={13} />}
                {!previewLoading && preview?.available && (
                  preview.triggered
                    ? <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    : <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                )}
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {previewLoading
                    ? (isEventRule
                      ? 'Checking the stream\u2019s current state…'
                      : 'Checking against the latest forecast…')
                    : preview?.available
                      ? (preview.triggered
                        ? (preview.event_rule
                          ? 'Would trigger right now'
                          : 'Would trigger on the latest forecast')
                        : 'Would not trigger right now')
                      : (preview?.reason || 'No forecast to preview against yet')}
                </Typography>
              </Stack>
              {preview?.available && (
                <>
                  {/* Event rules watch event state, not a forecast path —
                      there is nothing to chart. */}
                  {!preview.event_rule && (
                    <PreviewChart preview={preview} condition={condition} />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {preview.condition_label}
                    {preview.detail ? ` · ${preview.detail}` : ''}
                  </Typography>
                </>
              )}
            </Paper>
          )}

          <Divider />

          {/* ---- severity + cooldown ---- */}
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                SEVERITY
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={draft.severity}
                onChange={(e, v) => v && setDraft((d) => ({ ...d, severity: v }))}
              >
                <ToggleButton value="warning" sx={{ px: 1.5, textTransform: 'none' }}>
                  <WarningAmberIcon sx={{ fontSize: 15, mr: 0.5 }} /> Warning
                </ToggleButton>
                <ToggleButton value="critical" sx={{ px: 1.5, textTransform: 'none' }}>
                  <CrisisAlertIcon sx={{ fontSize: 15, mr: 0.5 }} /> Critical
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Re-alert at most</InputLabel>
              <Select
                label="Re-alert at most"
                value={draft.cooldown_minutes}
                onChange={(e) => setDraft((d) => ({ ...d, cooldown_minutes: e.target.value }))}
              >
                {(isFleet ? FLEET_COOLDOWNS : COOLDOWNS).map((c) => (
                  <MenuItem key={c.minutes} value={c.minutes}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* ---- delivery channels ---- */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
              DELIVERY
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label="My notification preferences"
                variant={draft.channels ? 'outlined' : 'filled'}
                color={draft.channels ? 'default' : 'primary'}
                onClick={() => setDraft((d) => ({ ...d, channels: null }))}
                sx={{ mr: 0.5 }}
              />
              {PINNABLE_CHANNELS.map((key) => {
                const active = (draft.channels || []).includes(key);
                return (
                  <Tooltip key={key} title={CHANNEL_META[key]?.label || key}>
                    <IconButton
                      size="small"
                      onClick={() => toggleChannel(key)}
                      sx={{
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: active ? alpha(theme.palette.primary.main, 0.5) : 'transparent',
                        bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                        opacity: active ? 1 : 0.45,
                      }}
                    >
                      <ChannelIcon type={key} size={17} />
                    </IconButton>
                  </Tooltip>
                );
              })}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {draft.channels
                ? 'Pinned: this alert always goes to the selected channels.'
                : 'Each recipient\u2019s own Settings \u203A Notifications choices apply.'}
            </Typography>
          </Box>

          {/* ---- recipients (org workspaces only) ---- */}
          {orgMembers?.length > 0 && (
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                <GroupOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  ALSO NOTIFY
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {orgMembers
                  .filter((m) => m.user_id !== currentUserId)
                  .map((m) => {
                    const active = (draft.recipients || []).includes(m.user_id);
                    return (
                      <Chip
                        key={m.user_id}
                        size="small"
                        label={m.name || m.email}
                        variant={active ? 'filled' : 'outlined'}
                        color={active ? 'primary' : 'default'}
                        onClick={() => toggleRecipient(m.user_id)}
                      />
                    );
                  })}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                You always receive your own alerts; teammates you pick get them too.
              </Typography>
            </Box>
          )}

          {saveError && <Alert severity="error">{saveError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose}>Cancel</Button>
        <Button
          size="small"
          variant="contained"
          disabled={!canSave}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : (editing ? 'Save changes' : 'Create alert')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// ---------------------------------------------------------------------------
// One rule card
// ---------------------------------------------------------------------------

const AlertRow = ({ alert, goalType, onToggle, onEdit, onDelete }) => {
  const theme = useTheme();
  const sevColor = alert.severity === 'critical'
    ? theme.palette.error.main : theme.palette.warning.main;
  const last = alert.last_result;
  const lastFired = fmtWhen(alert.last_triggered_at);
  const lastEval = fmtWhen(alert.last_evaluated_at);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        opacity: alert.enabled ? 1 : 0.55,
        borderLeft: `3px solid ${alert.enabled ? sevColor : theme.palette.divider}`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
              {alert.name}
            </Typography>
            <Chip
              size="small"
              label={alert.severity}
              sx={{
                height: 18, fontSize: 10.5, textTransform: 'uppercase',
                fontWeight: 700,
                color: sevColor,
                bgcolor: alpha(sevColor, 0.12),
              }}
            />
            {(alert.channels || []).map((c) => (
              <Tooltip key={c} title={CHANNEL_META[c]?.label || c}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <ChannelIcon type={c} size={14} />
                </Box>
              </Tooltip>
            ))}
            {(alert.recipients?.length || 0) > 0 && (
              <Tooltip title={`Also notifies ${alert.recipients.length} teammate${alert.recipients.length === 1 ? '' : 's'}`}>
                <Chip
                  size="small" icon={<GroupOutlinedIcon sx={{ fontSize: 13 }} />}
                  label={`+${alert.recipients.length}`}
                  variant="outlined" sx={{ height: 18, fontSize: 10.5 }}
                />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            {conditionSentence(alert.condition, '', goalType)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
            {last && (
              last.triggered
                ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <WarningAmberIcon sx={{ fontSize: 13, color: sevColor }} />
                    <Typography variant="caption" sx={{ color: sevColor, fontWeight: 600 }}>
                      Triggered{lastFired ? ` · ${lastFired}` : ''}
                    </Typography>
                  </Stack>
                )
                : (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <RadioButtonUncheckedIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary">
                      Quiet{lastEval ? ` · checked ${lastEval}` : ''}
                    </Typography>
                  </Stack>
                )
            )}
            {!last && (
              <Typography variant="caption" color="text.secondary">
                Not evaluated yet — waits for the next forecast
              </Typography>
            )}
            {alert.trigger_count > 0 && (
              <Typography variant="caption" color="text.secondary">
                · fired {alert.trigger_count}×
              </Typography>
            )}
            {last?.detail && (
              <Tooltip title={last.detail}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 260 }}>
                  · {last.detail}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Tooltip title="Edit">
          <IconButton size="small" onClick={onEdit}>
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={onDelete}>
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Switch
          size="small"
          checked={!!alert.enabled}
          onChange={(e) => onToggle(e.target.checked)}
        />
      </Stack>
    </Paper>
  );
};


// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

const GoalAlertsPanel = ({ goalId, goalType }) => {
  const dispatch = useDispatch();
  const [items, setItems] = useState(null);      // null = loading
  const [hasForecast, setHasForecast] = useState(true);
  // Event streams for the builder's stream picker (event-native rules).
  const [eventStreams, setEventStreams] = useState([]);
  // The list response is authoritative on goal type (covers direct links
  // where the parent hasn't loaded the goal yet).
  const [serverGoalType, setServerGoalType] = useState(null);
  const effectiveGoalType = serverGoalType || goalType || 'forecast_value';
  const [error, setError] = useState(null);
  const [builder, setBuilder] = useState(null);  // { alert|null }
  const currentUserId = localStorage.getItem('USER_ID');

  const alertsState = useSelector((s) => s.discovery_alerts);
  const membersState = useSelector((s) => s.discovery_alert_org_members);
  const toggleState = useSelector((s) => s.discovery_toggle_alert);
  const deleteState = useSelector((s) => s.discovery_delete_alert);

  const load = useCallback(() => {
    dispatch(alertsRequest({ goalId }));
  }, [dispatch, goalId]);

  useEffect(() => { load(); }, [load]);

  // Reset the list slice on unmount so a later goal's panel starts from
  // the loading spinner instead of another goal's rules.
  useEffect(() => () => { dispatch(alertsClear()); }, [dispatch]);

  // List landed — mirror it into local state (local `items` stays the
  // render source of truth so optimistic toggle/delete still work).
  useEffect(() => {
    const data = alertsState?.response;
    if (!data) return;
    setItems(data.items || []);
    setHasForecast(!!data.has_forecast);
    setEventStreams(data.event_streams || []);
    if (data.goal_type) setServerGoalType(data.goal_type);
    setError(null);
  }, [alertsState?.response]);

  useEffect(() => {
    if (!alertsState?.error) return;
    setError(alertsState.error);
    setItems([]);
  }, [alertsState?.error]);

  // Live: the engine publishes `alerts.evaluated` on this goal's channel
  // after every evaluation pass (the page subscribes to goal:<id>).
  useRealtimeRefresh(eventPrefix('alerts.'), () => load(), [goalId]);

  // Org members for the recipients picker — best-effort; absence of an
  // org simply hides the section (the saga swallows the failure).
  useEffect(() => { dispatch(alertOrgMembersRequest()); }, [dispatch]);
  const orgMembers = membersState?.response?.members || [];

  const handleToggle = (alert, enabled) => {
    setItems((prev) => (prev || []).map(
      (a) => (a.alert_id === alert.alert_id ? { ...a, enabled } : a),
    ));
    dispatch(toggleAlertRequest({ goalId, alertId: alert.alert_id, enabled }));
  };

  const handleDelete = (alert) => {
    setItems((prev) => (prev || []).filter((a) => a.alert_id !== alert.alert_id));
    dispatch(deleteAlertRequest({ goalId, alertId: alert.alert_id }));
  };

  // Optimistic toggle/delete failed — reload to revert to server truth
  // (matches the old catch { load(); } blocks).
  useEffect(() => {
    if (toggleState?.error) load();
  }, [toggleState?.error, load]);

  useEffect(() => {
    if (deleteState?.error) load();
  }, [deleteState?.error, load]);

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <NotificationsActiveIcon sx={{ fontSize: 18 }} />
            <Typography variant="overline" sx={{ letterSpacing: 0.6, color: 'text.secondary' }}>
              Alerts
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {isFleetGoal(effectiveGoalType)
              ? 'Rules checked on every scoring run — and, for event rules, the moment an event fires or the goal enters shock mode. Delivery uses your notification channels — in-app, email, Slack, Teams, PagerDuty, webhooks.'
              : 'Rules checked on every forecast — and, for event rules, the moment an event fires or the goal enters shock mode. Delivery uses your notification channels — in-app, email, Slack, Teams, PagerDuty, webhooks.'}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setBuilder({ alert: null })}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          New alert
        </Button>
      </Stack>
      <Divider />

      {!hasForecast && !isFleetGoal(effectiveGoalType) && (
        <Alert severity="info" variant="outlined">
          No forecast yet — alerts arm as soon as a deployed champion produces
          its first forecast. You can create rules now.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      {items === null && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {items?.length === 0 && !error && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {isFleetGoal(effectiveGoalType)
              ? (effectiveGoalType === 'rank_by_risk'
                ? 'No alerts yet. Create one — e.g. \u201cpage me when any member\u2019s failure risk tops 80%\u201d.'
                : 'No alerts yet. Create one — e.g. \u201cwarn me when any member\u2019s predicted growth tops 10%\u201d.')
              : isClassificationGoal(effectiveGoalType)
                ? (effectiveGoalType === 'anomaly'
                  ? 'No alerts yet. Create one — e.g. \u201cpage me when the anomaly likelihood tops 80%\u201d.'
                  : 'No alerts yet. Create one — e.g. \u201cwarn me when the upward-trend likelihood tops 80%\u201d.')
                : 'No alerts yet. Create one — e.g. \u201cwarn me when the forecast tops a level\u201d or \u201cpage me when the model gets unsure\u201d.'}
          </Typography>
        </Box>
      )}

      <Stack spacing={1}>
        {(items || []).map((a) => (
          <AlertRow
            key={a.alert_id}
            alert={a}
            goalType={effectiveGoalType}
            onToggle={(on) => handleToggle(a, on)}
            onEdit={() => setBuilder({ alert: a })}
            onDelete={() => handleDelete(a)}
          />
        ))}
      </Stack>

      {builder && (
        <AlertBuilderDialog
          open
          goalId={goalId}
          goalType={effectiveGoalType}
          alert={builder.alert}
          orgMembers={orgMembers}
          currentUserId={currentUserId}
          eventStreams={eventStreams}
          onClose={() => setBuilder(null)}
          onSaved={load}
        />
      )}
    </Stack>
  );
};

export default GoalAlertsPanel;
